"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import type { Label as LabelPrimitive } from "radix-ui";
import { Slot } from "radix-ui";
import { Controller, FormProvider, useFormContext, useFormState, type ControllerProps, type FieldPath, type FieldValues } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Label } from "@osmix/ui/label";
const Form = FormProvider;
type FormFieldContextValue<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = {
  name: TName;
};
const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);
const FormField = t0 => {
  const $ = _c(9);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props.name) {
    t1 = {
      name: props.name
    };
    $[2] = props.name;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props) {
    t2 = <Controller {...props} />;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <FormFieldContext.Provider value={t1}>{t2}</FormFieldContext.Provider>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const useFormField = () => {
  const $ = _c(13);
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const {
    getFieldState
  } = useFormContext();
  let t0;
  if ($[0] !== fieldContext.name) {
    t0 = {
      name: fieldContext.name
    };
    $[0] = fieldContext.name;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const formState = useFormState(t0);
  let t1;
  if ($[2] !== fieldContext.name || $[3] !== formState || $[4] !== getFieldState) {
    t1 = getFieldState(fieldContext.name, formState);
    $[2] = fieldContext.name;
    $[3] = formState;
    $[4] = getFieldState;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const fieldState = t1;
  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }
  const {
    id
  } = itemContext;
  const t2 = `${id}-form-item`;
  const t3 = `${id}-form-item-description`;
  const t4 = `${id}-form-item-message`;
  let t5;
  if ($[6] !== fieldContext.name || $[7] !== fieldState || $[8] !== id || $[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = {
      id,
      name: fieldContext.name,
      formItemId: t2,
      formDescriptionId: t3,
      formMessageId: t4,
      ...fieldState
    };
    $[6] = fieldContext.name;
    $[7] = fieldState;
    $[8] = id;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
type FormItemContextValue = {
  id: string;
};
const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);
function FormItem(t0) {
  const $ = _c(13);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const id = React.useId();
  let t1;
  if ($[3] !== id) {
    t1 = {
      id
    };
    $[3] = id;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== className) {
    t2 = cn("grid gap-2", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== t2) {
    t3 = <div data-slot="form-item" className={t2} {...props} />;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t1 || $[11] !== t3) {
    t4 = <FormItemContext.Provider value={t1}>{t3}</FormItemContext.Provider>;
    $[10] = t1;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function FormLabel(t0) {
  const $ = _c(10);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    error,
    formItemId
  } = useFormField();
  const t1 = !!error;
  let t2;
  if ($[3] !== className) {
    t2 = cn("data-[error=true]:text-destructive", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== formItemId || $[6] !== props || $[7] !== t1 || $[8] !== t2) {
    t3 = <Label data-slot="form-label" data-error={t1} className={t2} htmlFor={formItemId} {...props} />;
    $[5] = formItemId;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function FormControl(t0) {
  const $ = _c(7);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  const {
    error,
    formItemId,
    formDescriptionId,
    formMessageId
  } = useFormField();
  const t1 = !error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`;
  const t2 = !!error;
  let t3;
  if ($[2] !== formItemId || $[3] !== props || $[4] !== t1 || $[5] !== t2) {
    t3 = <Slot.Root data-slot="form-control" id={formItemId} aria-describedby={t1} aria-invalid={t2} {...props} />;
    $[2] = formItemId;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function FormDescription(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    formDescriptionId
  } = useFormField();
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-sm text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== formDescriptionId || $[6] !== props || $[7] !== t1) {
    t2 = <p data-slot="form-description" id={formDescriptionId} className={t1} {...props} />;
    $[5] = formDescriptionId;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function FormMessage(t0) {
  const $ = _c(10);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    error,
    formMessageId
  } = useFormField();
  const body = error ? String(error?.message ?? "") : props.children;
  if (!body) {
    return null;
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-sm text-destructive", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== body || $[6] !== formMessageId || $[7] !== props || $[8] !== t1) {
    t2 = <p data-slot="form-message" id={formMessageId} className={t1} {...props}>{body}</p>;
    $[5] = body;
    $[6] = formMessageId;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export { useFormField, Form, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField };